/**
 * Data Card Component
 * Metric/stat card with value, trend, and icon
 */

import React from 'react';
import { Card, Statistic } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { spacing, colors, shadows, borderRadius } from '../../theme/designTokens';

/**
 * DataCard Component
 *
 * @param {string} title - Card title
 * @param {number|string} value - Main value
 * @param {string} prefix - Value prefix (e.g., $)
 * @param {string} suffix - Value suffix (e.g., %)
 * @param {ReactNode} icon - Icon component
 * @param {number} trend - Trend percentage (positive/negative)
 * @param {string} trendLabel - Trend description
 * @param {string} valueColor - Custom value color
 * @param {boolean} loading - Loading state
 * @param {function} onClick - Click handler
 * @param {object} style - Additional styles
 */
function DataCard({
  title,
  value,
  prefix,
  suffix,
  icon,
  trend,
  trendLabel,
  valueColor,
  loading = false,
  onClick,
  style = {},
}) {
  const isTrendPositive = trend > 0;
  const isTrendNegative = trend < 0;

  return (
    <Card
      loading={loading}
      hoverable={!!onClick}
      onClick={onClick}
      style={{
        borderRadius: borderRadius.md,
        boxShadow: shadows.sm,
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
      bodyStyle={{
        padding: spacing.lg,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {/* Statistic */}
        <Statistic
          title={title}
          value={value}
          prefix={prefix}
          suffix={suffix}
          valueStyle={{
            color: valueColor || colors.text.primary,
            fontSize: 24,
          }}
        />

        {/* Icon */}
        {icon && (
          <div
            style={{
              fontSize: 32,
              color: colors.primary[500],
              opacity: 0.8,
            }}
          >
            {icon}
          </div>
        )}
      </div>

      {/* Trend indicator */}
      {trend !== null && trend !== undefined && (
        <div
          style={{
            marginTop: spacing.sm,
            fontSize: 12,
            color: isTrendPositive ? colors.status.pass : isTrendNegative ? colors.status.fail : colors.text.secondary,
          }}
        >
          {isTrendPositive && <ArrowUpOutlined style={{ marginRight: spacing.xxs }} />}
          {isTrendNegative && <ArrowDownOutlined style={{ marginRight: spacing.xxs }} />}
          {Math.abs(trend)}%
          {trendLabel && <span style={{ marginLeft: spacing.xs, color: colors.text.secondary }}>{trendLabel}</span>}
        </div>
      )}
    </Card>
  );
}

export default DataCard;
